/* =========================================================================
   CASCADE LIGHTING — <cascade-hero> (sections/cascade-hero.liquid)
   Fade slideshow ported from the prototype: autoplay with pause on hover /
   focus / hidden tab, play-pause, prev/next, dots, arrow keys, swipe,
   reduced motion = no autoplay, inert + aria-hidden on inactive slides.
   Theme editor: shopify:section:load re-creates the element (connectedCallback),
   shopify:section:unload removes it (disconnectedCallback clears timers),
   section:select pauses autoplay, block:select shows and holds that slide.
   ========================================================================= */
if (!customElements.get('cascade-hero')) {
  class CascadeHero extends HTMLElement {
    constructor() {
      super();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.timer = null;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.editorPaused = false;
      this.startX = 0;
      this.startY = 0;
      this.tracking = false;

      this.schedule = this.schedule.bind(this);
      this.onReducedMotion = (event) => this.setPlaying(!event.matches && this.autoplay);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-hero-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.playBtn = this.querySelector('[data-hero-play]');
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = Number(this.dataset.speed) || 6500;
      this.playing = this.autoplay && !this.reducedMotion.matches;

      if (this.slides.length < 2) return;

      this.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));
      this.playBtn?.addEventListener('click', () => this.setPlaying(!this.playing));

      this.addEventListener('mouseenter', () => { this.hoverPaused = true; clearTimeout(this.timer); });
      this.addEventListener('mouseleave', () => { this.hoverPaused = false; this.schedule(); });
      this.addEventListener('focusin', () => { this.focusPaused = true; clearTimeout(this.timer); });
      this.addEventListener('focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.focusPaused = false;
        this.schedule();
      });
      this.addEventListener('keydown', (event) => {
        if (event.target.matches('input, textarea, select')) return;
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.index - 1, true); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.index + 1, true); }
      });

      // Swipe (touch & pen)
      this.viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') return;
        this.tracking = true;
        this.startX = event.clientX;
        this.startY = event.clientY;
      });
      this.viewport.addEventListener('pointerup', (event) => {
        if (!this.tracking) return;
        this.tracking = false;
        const dx = event.clientX - this.startX;
        const dy = event.clientY - this.startY;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.2) this.go(this.index + (dx < 0 ? 1 : -1), true);
      });
      this.viewport.addEventListener('pointercancel', () => { this.tracking = false; });

      document.addEventListener('visibilitychange', this.schedule);
      this.reducedMotion.addEventListener('change', this.onReducedMotion);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionSelect);

      this.setPlaying(this.playing);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('visibilitychange', this.schedule);
      this.reducedMotion.removeEventListener('change', this.onReducedMotion);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionSelect);
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.playing && !this.hoverPaused && !this.focusPaused && !this.editorPaused && !document.hidden) {
        this.timer = setTimeout(() => this.go(this.index + 1, false), this.speed);
      }
    }

    go(i, fromUser) {
      const count = this.slides.length;
      this.index = (i + count) % count;
      this.viewport.setAttribute('aria-live', fromUser ? 'polite' : 'off');
      this.slides.forEach((slide, j) => {
        const active = j === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      this.dots.forEach((dot, j) => (j === this.index ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current')));
      this.dataset.theme = this.slides[this.index].dataset.theme || 'light';

      // Warm up the following slide's image
      const nextImg = this.slides[(this.index + 1) % count].querySelector('img');
      if (nextImg && nextImg.loading === 'lazy') nextImg.loading = 'eager';

      this.schedule();
    }

    setPlaying(state) {
      this.playing = state;
      if (this.playBtn) {
        this.playBtn.setAttribute('aria-label', state ? this.playBtn.dataset.labelPause : this.playBtn.dataset.labelPlay);
        this.playBtn.querySelector('use')?.setAttribute('href', state ? '#cl-icon-pause' : '#cl-icon-play');
      }
      this.schedule();
    }

    onSectionSelect(event) {
      if (!event.target.contains(this)) return;
      this.editorPaused = event.type === 'shopify:section:select';
      this.schedule();
    }

    onBlockSelect(event) {
      const i = this.slides.indexOf(event.target);
      if (i === -1) return;
      this.editorPaused = true;
      this.go(i, false);
    }

    onBlockDeselect(event) {
      if (!this.slides.includes(event.target)) return;
      this.editorPaused = false;
      this.schedule();
    }
  }

  customElements.define('cascade-hero', CascadeHero);
}
